import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ImagePlus, Upload, Loader2 } from "lucide-react";
import { api, errMsg, mediaUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function TextF({ label, value, onChange, hint, testid, type = "text" }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)} className="field-input" data-testid={testid} />
      {hint && <p className="mt-1 text-xs text-zinc-500">{hint}</p>}
    </div>
  );
}

export function AreaF({ label, value, onChange, hint, rows = 4, testid }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      <textarea rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="field-input" data-testid={testid} />
      {hint && <p className="mt-1 text-xs text-zinc-500">{hint}</p>}
    </div>
  );
}

export function LinesF({ label, value, onChange, testid }) {
  return (
    <AreaF label={label} testid={testid} hint="Her satıra bir madde yazın." rows={5}
      value={(value || []).join("\n")} onChange={(t) => onChange(t.split("\n").map((s) => s.trimStart()).filter((s, i, a) => s || i === a.length - 1))} />
  );
}

export function CheckF({ label, value, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 text-sm font-semibold text-zinc-800">
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-[color:var(--red)]" data-testid={testid} /> {label}
    </label>
  );
}

export function PairsF({ label, value, onChange, keys, labels, area = 1, testid }) {
  const list = value || [];
  const ids = useRef([]);
  const seq = useRef(0);
  while (ids.current.length < list.length) ids.current.push(`pair-${seq.current++}`);
  ids.current.length = list.length;
  const upd = (i, k, v) => onChange(list.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const remove = (i) => {
    ids.current.splice(i, 1);
    onChange(list.filter((_, j) => j !== i));
  };
  return (
    <div data-testid={testid}>
      <label className="field-label">{label}</label>
      <div className="space-y-3">
        {list.map((it, i) => (
          <div key={ids.current[i]} className="space-y-2 rounded-md border border-zinc-200 bg-zinc-50 p-3">
            <div className="flex gap-2">
              <input value={it[keys[0]] || ""} onChange={(e) => upd(i, keys[0], e.target.value)} placeholder={labels[0]} className="field-input" data-testid={`${testid}-${i}-a`} />
              <button type="button" onClick={() => remove(i)} className="rounded p-2 text-red-600 hover:bg-red-50" aria-label="Kaldır"><Trash2 className="h-4 w-4" /></button>
            </div>
            <textarea rows={area === 2 ? 6 : 3} value={it[keys[1]] || ""} onChange={(e) => upd(i, keys[1], e.target.value)} placeholder={labels[1]} className="field-input" data-testid={`${testid}-${i}-b`} />
          </div>
        ))}
      </div>
      <button type="button" onClick={() => onChange([...list, { [keys[0]]: "", [keys[1]]: "" }])} className="btn-outline mt-3 !py-2" data-testid={`${testid}-add`}>
        <Plus className="h-4 w-4" /> Ekle
      </button>
      {area === 2 && <p className="mt-1 text-xs text-zinc-500">Paragrafları boş satırla ayırın. Madde işareti için satırı "- " ile başlatın.</p>}
    </div>
  );
}

export function MediaPicker({ open, onClose, onPick }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const images = useMemo(() => (items || []).filter((m) => m.content_type.startsWith("image")), [items]);
  useEffect(() => {
    if (open) api.get("/admin/media").then((r) => setItems(r.data));
  }, [open]);
  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const r = await api.post("/admin/media", fd);
      onPick(r.data.url);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto" data-testid="media-picker">
        <DialogHeader><DialogTitle>Görsel seç</DialogTitle></DialogHeader>
        <label className="flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-zinc-300 p-4 text-sm hover:border-[color:var(--red)]">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Yeni görsel yükle (otomatik WebP'ye dönüştürülür)
          <input type="file" accept="image/*,video/mp4" className="sr-only" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid="media-picker-upload" />
        </label>
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
          {images.map((m) => (
            <button key={m.id} type="button" onClick={() => onPick(m.url)} className="aspect-square overflow-hidden rounded border border-zinc-200 hover:border-[color:var(--red)]">
              <img src={mediaUrl(m.url)} alt={m.filename} className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ImageF({ label, value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <label className="field-label">{label}</label>
      <div className="flex items-center gap-3">
        <div className="h-16 w-24 shrink-0 overflow-hidden rounded border border-zinc-200 bg-zinc-100">
          {value && <img src={mediaUrl(value)} alt="" className="h-full w-full object-cover" />}
        </div>
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} className="field-input" data-testid={testid} />
        <button type="button" onClick={() => setOpen(true)} className="btn-outline shrink-0 !px-3 !py-2" data-testid={`${testid}-pick`}><ImagePlus className="h-4 w-4" /></button>
      </div>
      <MediaPicker open={open} onClose={() => setOpen(false)} onPick={(u) => { onChange(u); setOpen(false); }} />
    </div>
  );
}

export function renderField(f, value, onChange) {
  return <FieldSwitch key={f.key} f={f} value={value} onChange={onChange} />;
}

const FAQ_KEYS = ["q", "a"];
const FAQ_LABELS = ["Soru", "Cevap"];
const SECTION_KEYS = ["heading", "body"];
const SECTION_LABELS = ["Başlık", "Metin"];

function SelectF({ f, value, onChange, testid }) {
  return (
    <div>
      <label className="field-label">{f.label}</label>
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="field-input" data-testid={testid}>
        {f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

const FIELD_RENDER = {
  area: (p, f) => <AreaF {...p} rows={f.rows || 4} />,
  lines: (p) => <LinesF {...p} />,
  check: (p) => <CheckF {...p} />,
  image: (p) => <ImageF {...p} />,
  number: (p) => <TextF {...p} type="number" />,
  faqs: (p) => <PairsF {...p} keys={FAQ_KEYS} labels={FAQ_LABELS} />,
  sections: (p) => <PairsF {...p} keys={SECTION_KEYS} labels={SECTION_LABELS} area={2} />,
  pairs: (p, f) => <PairsF {...p} keys={f.keys} labels={f.labels} />,
  select: (p, f) => <SelectF f={f} value={p.value} onChange={p.onChange} testid={p.testid} />,
};

function FieldSwitch({ f, value, onChange }) {
  const p = { label: f.label, value, onChange, hint: f.hint, testid: `field-${f.key}` };
  const render = FIELD_RENDER[f.type];
  return render ? render(p, f) : <TextF {...p} />;
}
